import { stageCatalog, calculateBusinessMetrics, formatCurrency } from '../popupQuote/catalog';

import { STAGE_KEYS, CENTRAL_STAGE_CONFIG as STAGE_CONFIG } from './assistantPlaybook';

export { STAGE_KEYS };

export const FIELD_ORDER = [
  'userName',
  'businessType',
  'negocio',
  'knowsTargetService',
  'isInvestigating',
  'isFaqCompleted',
  'isBusinessOnline',
  'hasOnlineSales',
  'missingAssets',
  'hasGoogleMyBusiness',
  'offer',
  'priorityOffering',
  'targetAudience',
  'objetivoPrincipal',
  'problemaPrincipal',
  'requestedService',
  'businessModelState',
  'brandStatus',
  'brandVision',
  'expectedOutcome',
  'currentAssets',
  'prospectos',
  'presupuestoMesa',
  'desiredTimeline',
  'ticket',
  'productionCost',
  'recurringPayments',
  'recurringValue',
  'newClients',
  'closeRate',
  'marketCpl',
  'qualifiedCpl',
  'marketingPercent',
  'userEmail',
  'userPhone',
  'isConfirmed',
];

export const FIELD_LABELS = {
  userName: 'su nombre',
  businessType: 'si es empresa o profesional independiente',
  negocio: 'nombre de la marca o proyecto',
  knowsTargetService: 'si ya sabe que servicio puntual necesita de Hitpoly',
  isInvestigating: 'si solo esta curioseando / investigando',
  isConfirmed: 'si el usuario confirmo el resumen de sus datos',
  isFaqCompleted: 'si termino de hacer preguntas y se retira',
  isBusinessOnline: 'si el negocio ya tiene presencia en internet',
  hasOnlineSales: 'si ya genera ventas por internet',
  missingAssets: 'activos que le faltan (web, redes, etc)',
  hasGoogleMyBusiness: 'si tiene Google My Business',
  offer: 'que productos o servicios vende',
  priorityOffering: 'producto o servicio a impulsar',
  targetAudience: 'a que publico busca llegar',
  objetivoPrincipal: 'meta o vision principal',
  userEmail: 'correo electronico',
  userPhone: 'WhatsApp o celular',
  problemaPrincipal: 'el freno principal hoy',
  requestedService: 'servicio puntual buscado',
  businessModelState: 'estado del modelo de negocio',
  brandStatus: 'estado de la marca',
  brandVision: 'percepcion visual deseada',
  expectedOutcome: 'lo que espera recibir al final',
  currentAssets: 'activos actuales',
  prospectos: 'canal de prospectos actuales',
  presupuestoMesa: 'presupuesto disponible',
  desiredTimeline: 'tiempo para estar listo',
  ticket: 'valor del ticket promedio',
  productionCost: 'costo operativo por cliente',
  recurringPayments: 'pagos de recurrencia',
  recurringValue: 'valor de la recurrencia',
  newClients: 'meta de clientes nuevos/mes',
  closeRate: 'tasa de cierre comercial',
  marketCpl: 'CPL estimado o real crudo',
  qualifiedCpl: 'CPL calificado',
  marketingPercent: 'porcentaje de reinversion',
};

const GREETING_PATTERN = /^(hola|buenas|hey|hello|alo|holi|gola|hi)[!. ]*$/i;
const NEGATION_PATTERN = /(no te dije|no dije|nunca te dije|no es eso|te equivocaste|estas confundido|de donde sacaste)/i;
const NUMERIC_FIELDS = new Set([
  'presupuestoMesa',
  'ticket',
  'productionCost',
  'recurringPayments',
  'recurringValue',
  'newClients',
  'closeRate',
  'marketCpl',
  'qualifiedCpl',
  'marketingPercent',
]);



export const createInitialAnswers = () => ({
  userName: '',
  businessType: '',
  negocio: '',
  knowsTargetService: '',
  isInvestigating: '',
  isConfirmed: '',
  isFaqCompleted: '',
  isBusinessOnline: '',
  hasOnlineSales: '',
  missingAssets: '',
  hasGoogleMyBusiness: '',
  offer: '',
  priorityOffering: '',
  targetAudience: '',
  objetivoPrincipal: '',
  userEmail: '',
  userPhone: '',
  problemaPrincipal: '',
  requestedService: '',
  businessModelState: '',
  brandStatus: '',
  brandVision: '',
  expectedOutcome: '',
  currentAssets: '',
  prospectos: '',
  presupuestoMesa: '',
  desiredTimeline: '',
  ticket: '',
  productionCost: '',
  recurringPayments: '',
  recurringValue: '',
  newClients: '',
  closeRate: '',
  marketCpl: '',
  qualifiedCpl: '',
  marketingPercent: '',
});

export const extractNumber = (text) => {
  if (!text) return '';
  // Eliminar puntos de miles (comun en Latinoamerica/Europa: 1.000 -> 1000)
  // pero solo si hay un punto seguido de exactamente 3 digitos y luego nada o mas numeros
  let cleaned = String(text)
    .replace(/\.(?=\d{3}(?!\d))/g, '') 
    .replace(/[$,]/g, ''); // Eliminar simbolos de moneda y comas
  
  // Capturar opcionalmente el simbolo de %
  const match = cleaned.match(/-?\d+(?:\.\d+)?%?/);
  return match ? match[0] : '';
};

export const cleanText = (text) => String(text || '').trim();

export const normalizeFieldValue = (field, value) => {
  if (value == null) return '';
  const raw = cleanText(value);
  if (!raw) return '';

  const numericFields = [
    'presupuestoMesa',
    'ticket',
    'productionCost',
    'recurringPayments',
    'recurringValue',
    'newClients',
    'closeRate',
    'marketCpl',
    'qualifiedCpl',
    'marketingPercent',
  ];

  if (numericFields.includes(field)) {
    return extractNumber(raw);
  }

  return raw;
};

export const detectObjective = (text) => {
  const value = cleanText(text).toLowerCase();
  if (!value) return '';

  if (
    value.includes('marca') ||
    value.includes('branding') ||
    value.includes('presencia') ||
    value.includes('identidad') ||
    value.includes('salir al mercado')
  ) {
    return 'Construir marca';
  }

  if (
    value.includes('trafico') ||
    value.includes('lead') ||
    value.includes('prospecto') ||
    value.includes('demanda') ||
    value.includes('seo') ||
    value.includes('google ads') ||
    value.includes('redes')
  ) {
    return 'Captar mas clientes';
  }

  if (
    value.includes('nutrir') ||
    value.includes('seguimiento') ||
    value.includes('whatsapp') ||
    value.includes('email') ||
    value.includes('calificar')
  ) {
    return 'Nutrir y madurar oportunidades';
  }

  if (
    value.includes('venta') ||
    value.includes('cerrar') ||
    value.includes('cierre') ||
    value.includes('conversion') ||
    value.includes('setter') ||
    value.includes('propuesta')
  ) {
    return 'Ordenar ventas';
  }

  if (
    value.includes('automat') ||
    value.includes('escalar') ||
    value.includes('operacion') ||
    value.includes('crm') ||
    value.includes('integracion')
  ) {
    return 'Escalar operacion';
  }

  return '';
};

const detectUserName = (text) => {
  const value = cleanText(text);
  if (!value) return '';

  // Filtro de seguridad: No aceptar frases de investigación o botones conocidos
  const lower = value.toLowerCase();
  if (
    /sólo investigaba|solo investigaba|investigar|curioseando|mirando|buscando info|explorando/i.test(lower) ||
    /nada más|por ahora no|solo mirar/i.test(lower)
  ) {
    return '';
  }

  const patterns = [
    /soy ([a-z][\w\s-]{1,30})/i,
    /me llamo ([a-z][\w\s-]{1,30})/i,
    /mi nombre es ([a-z][\w\s-]{1,30})/i,
    /llamame ([a-z][\w\s-]{1,30})/i,
    /puedes llamarme ([a-z][\w\s-]{1,30})/i,
    /aqui ([a-z][\w\s-]{1,30})/i
  ];

  for (const pattern of patterns) {
    const match = value.match(pattern);
    if (match?.[1]) return cleanText(match[1].replace(/[.?!]+$/, ''));
  }

  // Si es un texto corto y no es saludo ni negacion, podria ser el nombre directo
  // Pero bajamos el limite de longitud y añadimos mas filtros
  if (
    value.length > 2 && 
    value.length < 15 && 
    !GREETING_PATTERN.test(value) && 
    !/no|si|ya|andando|nada/.test(lower) &&
    !/[\u{1F600}-\u{1F64F}]/u.test(value) // No aceptar emojis como nombres directos
  ) {
     return value;
  }

  return '';
};

const detectRequestedService = (text) => {
  const value = cleanText(text).toLowerCase();
  if (!value) return '';
  if (/marca|branding|logo|web|pagina|redes/.test(value)) return 'Crear o reforzar marca';
  if (/campa|meta ads|google ads|publicidad|seo|blog|reels|contenido/.test(value)) return 'Activar atraccion';
  if (/correo|email|whatsapp|nutricion|crm|seguimiento|lead frio/.test(value)) return 'Nutrir oportunidades';
  if (/setter|cierre|conversion|propuesta|reunion|no-show|objecion/.test(value)) return 'Mejorar conversion';
  if (/automatizacion|integracion|software|ia|dashboard|reportes|operacion/.test(value)) return 'Escalar operacion';
  return '';
};

const detectBusinessModelState = (text) => {
  const value = cleanText(text).toLowerCase();
  if (!value) return '';
  if (/no tengo el negocio montado|aun no esta montado|estoy empezando|desde cero|todavia no salgo|aun no existe|estoy en proceso|lo estoy montando|apenas arranco/.test(value)) {
    return 'Se esta montando';
  }
  if (/ya existe|ya operamos|ya vendemos|ya tenemos clientes|ya esta andando|ya esta operando|ya esta en marcha|ya esta funcionando|ya tengo clientes/.test(value)) {
    return 'Ya existe';
  }
  return '';
};

const detectBrandStatus = (text) => {
  const value = cleanText(text).toLowerCase();
  if (!value) return '';
  if (/tengo todo creado|todo creado|marca creada|ya esta todo|ya tengo web|ya tengo redes|ya tengo logo|tengo todo credo/.test(value)) {
    return 'Lista o avanzada';
  }
  if (/me falta|no tengo web|no tengo redes|no tengo logo|falta crear|desde cero/.test(value)) {
    return 'Incompleta';
  }
  return '';
};

const detectBusinessType = (text) => {
  const value = cleanText(text).toLowerCase();
  if (/empresa constituida|empresa ya creada|somos una empresa|una empresa|sociedad|corporacion/.test(value)) return 'Empresa constituida';
  if (/profesional independiente|freelance|autonomo|trabajo solo|marca personal|soy solo yo/.test(value)) return 'Profesional independiente';
  return '';
};

const detectIsOnline = (text) => {
  const value = cleanText(text).toLowerCase();
  if (/ya tiene presencia|si tiene presencia|ya existe en internet|ya tengo web|ya tengo redes|si ya existe|ya estoy online|ya estamos online/.test(value)) return 'Si';
  if (/no tiene presencia|no existe aun|desde cero|no tengo nada|no estoy online|no estamos online|partiendo de cero/.test(value)) return 'No';
  return '';
};

const detectHasSales = (text) => {
  const value = cleanText(text).toLowerCase();
  if (/ya genera ventas|ya vendo|si vendo|ya tengo clientes|si ya genero|ya vendemos/.test(value)) return 'Si';
  if (/no genera ventas|aun no vendo|no tengo clientes|todavia no vendo|no vendo nada/.test(value)) return 'No';
  return '';
};

export const determineJourneyStage = (answers = {}) => {
  // Discovery Phase fields checks
  const hasBasicLeadInfo = cleanText(answers.userName) && cleanText(answers.userEmail) && cleanText(answers.userPhone);
  const hasBasicBusinessInfo = cleanText(answers.negocio) && cleanText(answers.businessType) && cleanText(answers.offer);
  
  // If we haven't finished Discovery, stay in BRAND (which acts as Discovery stage)
  if (!hasBasicLeadInfo || !hasBasicBusinessInfo || !cleanText(answers.objetivoPrincipal)) {
    return STAGE_KEYS.BRAND;
  }

  const objective = cleanText(answers.objetivoPrincipal).toLowerCase();
  const requested = cleanText(answers.requestedService).toLowerCase();
  const isOnline = cleanText(answers.isBusinessOnline).toLowerCase();

  // ROUTING LOGIC
  if (isOnline === 'no' || objective.includes('marca') || objective.includes('identidad')) {
    return STAGE_KEYS.BRAND;
  }

  if (objective.includes('captar') || objective.includes('cliente') || objective.includes('trafico') || objective.includes('publicidad')) {
    return STAGE_KEYS.ATTRACTION;
  }

  if (objective.includes('nutrir') || objective.includes('seguimiento') || objective.includes('whatsapp') || objective.includes('email')) {
    return STAGE_KEYS.NURTURING;
  }

  if (objective.includes('venta') || objective.includes('cerrar') || objective.includes('setter') || objective.includes('conversion')) {
    return STAGE_KEYS.CONVERSION;
  }

  if (objective.includes('automat') || objective.includes('escalar') || objective.includes('operacion') || objective.includes('software')) {
    return STAGE_KEYS.SCALE;
  }

  return STAGE_KEYS.ATTRACTION; // Default expert if discovery is done but niche is broad
};

export const getStageConfig = (answers = {}) => STAGE_CONFIG[determineJourneyStage(answers)];

export const getMissingFields = (answers) => {
  const config = getStageConfig(answers);
  return config.questionOrder.filter((field) => !cleanText(answers[field]));
};

export const getMissingCoreFields = (answers) => {
  const config = getStageConfig(answers);
  return config.coreFields.filter((field) => !cleanText(answers[field]));
};

// Mapa de razones técnicas por campo — le dice a la IA POR QUÉ necesita el dato, no CÓMO preguntarlo
const FIELD_REASONS = {
  userName: 'personalizar la asesoría y humanizar la conversación',
  userEmail: 'enviarle el análisis completo al terminar la sesión',
  userPhone: 'mantener el contacto si la sesión se interrumpe',
  businessType: 'saber si aplica estrategia de empresa o marca personal',
  negocio: 'mencionar su marca correctamente durante la asesoría',
  isInvestigating: 'saber que no debemos forzar la venta sino solo informar',
  isConfirmed: 'saber si ya reviso el resumen de sus datos correctamente',
  isBusinessOnline: 'determinar si parte de cero o ya tiene base digital',
  hasOnlineSales: 'evaluar si el foco es construir o escalar ventas',
  missingAssets: 'identificar qué activos de marca faltan por construir',
  hasGoogleMyBusiness: 'detectar oportunidad de visibilidad local gratuita',
  offer: 'entender qué vende para recomendar el canal correcto',
  priorityOffering: 'concentrar la estrategia en el producto de mayor impacto',
  targetAudience: 'definir la segmentación correcta de las campañas',
  objetivoPrincipal: 'encaminar la asesoría hacia la etapa correcta del journey',
  brandStatus: 'determinar si la marca está lista para atraer o necesita refuerzo',
  brandVision: 'alinear la propuesta estética con su visión de marca',
  expectedOutcome: 'définir el éxito esperado al final de este proceso',
  problemaPrincipal: 'detectar la fuga principal de dinero o crecimiento',
  requestedService: 'priorizar el servicio de mayor urgencia para cotizar',
  presupuestoMesa: 'validar si la inversión cubre el mínimo para ver resultados',
  desiredTimeline: 'planificar el ritmo de implementación adecuado',
  prospectos: 'entender de dónde vienen los contactos actuales',
  ticket: 'calcular el margen y el CPL máximo viable',
  productionCost: 'calcular la ganancia bruta real por cliente',
  recurringPayments: 'estimar el valor de vida del cliente (LTV)',
  recurringValue: 'calcular el ingreso recurrente total por cliente',
  newClients: 'establecer la meta de crecimiento mensual',
  closeRate: 'calcular cuántos leads se necesitan para alcanzar la meta',
  marketCpl: 'validar si el CPL actual crudo permite escalar con margen positivo',
  qualifiedCpl: 'diferenciar a los simples leads de las reuniones o citas agendadas reales',
  marketingPercent: 'estimar la reinversión sostenible en crecimiento',
};

export const getConversationChecklist = (answers) => {
  const config = getStageConfig(answers);
  const missing = config.questionOrder.filter((field) => !cleanText(answers[field]));
  const userName = cleanText(answers.userName) || 'el usuario';

  return missing.map(field => {
    const label = FIELD_LABELS[field] || field;
    const reason = FIELD_REASONS[field] || 'completar el diagnóstico estratégico';
    return `- DATO NECESARIO: "${label}" → POR QUÉ: ${reason}`;
  }).join('\n');
};

const detectAskedFieldFromAssistant = (assistantText = '', answers = {}) => {
  const lower = cleanText(assistantText).toLowerCase();
  const config = getStageConfig(answers);
  const fields = [...config.questionOrder, ...config.coreFields];

  return fields.find((field) => {
    const question = cleanText(config.questions[field]).toLowerCase();
    const label = cleanText(FIELD_LABELS[field]).toLowerCase();
    return (question && lower.includes(question.slice(0, 18))) || (label && lower.includes(label));
  }) || '';
};

const canAssignBareNumberToField = (field, text) => {
  if (!NUMERIC_FIELDS.has(field)) return false;
  const lower = cleanText(text).toLowerCase();
  if (!extractNumber(text)) return false;
  if (/(ticket|presupuesto|cliente|lead|cpl|cierre|porcentaje|marketing|producci|recompra|recurr)/.test(lower)) {
    return true;
  }
  return lower.length <= 18;
};

const maybeExtractBusiness = (message) => {
  const text = cleanText(message);
  const lower = text.toLowerCase();
  const patterns = [
    /mi negocio(?: se llama| es)?\s+([a-z0-9][\w\s-]{1,60})/i,
    /se llama\s+([a-z0-9][\w\s-]{1,60})/i,
    /nos dedicamos a\s+(.+)/i,
    /vendemos\s+(.+)/i,
    /me dedico a\s+(.+)/i,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match?.[1]) return cleanText(match[1].replace(/[.?!]+$/, ''));
  }

  if (GREETING_PATTERN.test(text) || text.length < 12) return '';
  if (NEGATION_PATTERN.test(lower)) return '';
  return '';
};

const buildCorrectionPatch = (message, askedField) => {
  const lower = cleanText(message).toLowerCase();
  const patch = {};

  if (/mi negocio no se llama|no se llama/.test(lower)) {
    patch.negocio = '';
  }

  if (/no te dije eso|te dije otra cosa|de donde sacaste/.test(lower) && askedField) {
    patch[askedField] = '';
  }

  if (/no sabemos el cpl|no se el cpl|no sabemos el costo por lead/.test(lower)) {
    patch.marketCpl = '';
  }

  if (/no el presupuesto|no es el presupuesto/.test(lower)) {
    patch.presupuestoMesa = '';
  }

  if (/no el ticket|no es el ticket|el ticket es/.test(lower)) {
    patch.ticket = '';
  }

  return patch;
};

const detectQuestionIntent = (message = '') => {
  const lower = cleanText(message).toLowerCase();
  if (!lower.includes('?') && !/^(que|cual|como|por que|porque|para que|de donde)/.test(lower)) {
    return '';
  }

  if (/cual problema principal|que problema principal|que quieres decir/.test(lower)) return 'problemaPrincipal';
  if (/que es el ticket|que ticket|cual ticket/.test(lower)) return 'ticket';
  if (/que es cpl|cual es cpl|costo por lead/.test(lower)) return 'marketCpl';
  if (/tasa de cierre|que es cierre/.test(lower)) return 'closeRate';
  if (/recurrencia|recompra/.test(lower)) return 'recurringPayments';
  return 'explain';
};

const isUserCorrecting = (message = '') => NEGATION_PATTERN.test(cleanText(message).toLowerCase()) || /no se llama|no es|te dije que|te estoy diciendo|respondi a tu pregunta/.test(cleanText(message).toLowerCase());

const isMeaningfulFreeText = (text = '') => {
  const value = cleanText(text);
  return value.length >= 8 && !GREETING_PATTERN.test(value);
};

export const inferFieldsFromMessage = (message, answers, context = {}) => {
  const text = cleanText(message);
  const lower = text.toLowerCase();
  const next = {};
  const lastAskedField = context.lastAskedField || detectAskedFieldFromAssistant(context.lastAssistantMessage, answers);
  const correctionPatch = buildCorrectionPatch(text, lastAskedField);

  Object.assign(next, correctionPatch);

  if (!text || GREETING_PATTERN.test(text)) {
    return next;
  }

  if (/sólo investigaba|solo investigaba|investigar|curioseando|mirando|buscando info|explorando/i.test(lower)) {
    next.isInvestigating = 'true';
  }

  // Detectar si el usuario acepta la auditoria rapida estando en modo FAQ
  if (answers.isInvestigating === 'true' && /si|claro|auditoria|dale|revis|me parece bien|adelante|de una|empecemos/i.test(lower) && !NEGATION_PATTERN.test(lower)) {
    next.isInvestigating = 'false';
  }

  // Detectar salida o final de dudas en modo investigador
  if (answers.isInvestigating === 'true' && /gracias|eso es todo|nada mas|no hay mas dudas|listo|adios|chao|me retiro|no gracias|no tengo dudas/i.test(lower)) {
    next.isFaqCompleted = 'true';
  }

  // Detectar confirmacion de resumen solo si ya no faltan campos (o casi todos llenos)
  const missingCount = getMissingFields(answers).length;
  if (missingCount <= 1 && /si|sí|correcto|bien|perfecto|excelente|avancemos|esta bien|asi es|tal cual/i.test(lower) && !NEGATION_PATTERN.test(lower)) {
    next.isConfirmed = 'true';
  }

  const explicitBusiness = maybeExtractBusiness(text);
  if (explicitBusiness) next.negocio = explicitBusiness;

  const name = detectUserName(text);
  if (name && !answers.userName) next.userName = name;

  const type = detectBusinessType(text);
  if (type) next.businessType = type;

  const online = detectIsOnline(text);
  if (online) next.isBusinessOnline = online;

  const sales = detectHasSales(text);
  if (sales) next.hasOnlineSales = sales;

  const objective = detectObjective(text);
  if (objective) next.objetivoPrincipal = objective;

  const modelState = detectBusinessModelState(text);
  if (modelState) next.businessModelState = modelState;

  const brand = detectBrandStatus(text);
  if (brand) next.brandStatus = brand;

  if (
    /(quiero|busco|necesito)\s+(crear una marca|construir marca|branding|mas clientes|mas prospectos|ordenar ventas|escalar)/i.test(text)
  ) {
    next.objetivoPrincipal = detectObjective(text);
  }

  const requestedService = detectRequestedService(text);
  if (requestedService) next.requestedService = requestedService;

  const businessModelState = detectBusinessModelState(text);
  if (businessModelState) {
    next.businessModelState = businessModelState;
    if (businessModelState === 'Ya existe') {
      next.brandStatus = 'Lista o avanzada';
    }
  }

  const brandStatus = detectBrandStatus(text);
  if (brandStatus) next.brandStatus = brandStatus;

  if (/marca\s+(premium|moderna|fuerte|sobria|seria|elegante)|quiero que se vea|quiero una marca|me imagino la marca/i.test(lower)) {
    next.brandVision = text;
  }

  if (/quiero recibir|quiero tener|me gustaria tener|espero tener|salir al mercado con/i.test(lower) && determineJourneyStage(answers) === STAGE_KEYS.BRAND) {
    next.expectedOutcome = text;
  }

  if (/campaña|campaña|ads|meta ads|google ads|trafico|atraer/i.test(lower)) {
    next.objetivoPrincipal = 'Generar leads o tráfico';
    next.requestedService = 'Tráfico, SEO, Ads';
  }

  if (
    /mi problema|lo que me frena|lo que nos frena|no logro|no podemos|se nos cae|no tenemos marca|no nos conocen|no convertimos/i.test(text)
  ) {
    next.problemaPrincipal = text;
  }

  if (!next.problemaPrincipal && lastAskedField === 'problemaPrincipal' && isMeaningfulFreeText(text) && !detectQuestionIntent(text)) {
    next.problemaPrincipal = text;
  }

  if (/cliente ideal|a quien|nuestro cliente|para marcas|para empresas|negocios que|personas con negocios|empresarios|empresas/i.test(lower)) {
    next.targetAudience = text;
  }

  if (/vendemos|vendo|ofrecemos|ofrezco|servicio principal|producto principal|hacemos/i.test(lower)) {
    next.offer = text;
  }

  if (/logo|web|pagina|redes|brochure|landing|crm|activos|ya tenemos/i.test(lower)) {
    next.currentAssets = text;
  }

  if (/prospect|lead|contacto|trafico|referido|whatsapp|instagram|facebook|google|seo|campana|anuncio/i.test(lower)) {
    next.prospectos = text;
  }

  if ((/presupuesto|invert|puedo poner|dispongo|mesa|cuento con/i.test(lower) || lastAskedField === 'presupuestoMesa') && extractNumber(text)) {
    next.presupuestoMesa = extractNumber(text);
  }

  if ((/ticket|cobro|cobra|paga|contrato|valor/i.test(lower) || lastAskedField === 'ticket') && !/cuesta|costo|gasto|operativo|producir|entregar/i.test(lower) && extractNumber(text)) {
    next.ticket = extractNumber(text);
  }

  // GUARD CRÍTICO A2/B5: solo asignar productionCost si el mensaje NO habla de lead/CPL/prospecto
  // Evita que "cada lead me cuesta 5" llene productionCost además de marketCpl
  const isAboutLeadCost = /lead|cpl|prospecto|contacto/i.test(lower);
  if (!isAboutLeadCost && ((/cuesta|costo|gasto|operativo|producir|entregar/i.test(lower) || lastAskedField === 'productionCost') && extractNumber(text))) {
    next.productionCost = extractNumber(text);
  }

  // recurringPayments: solo captura si hay confirmación explícita de recurrencia/renovación
  // NO debe activarse con "primer contrato de 3 meses" porque eso es duración, no frecuencia de pago
  const isAboutContractDuration = /primer contrato|contrato inicial|contrato de \d|por \d meses/i.test(lower);
  if (!isAboutContractDuration && ((/recompra|recurren|recurrent|renueva|veces al año|veces al ano|veces por ano|pagan mensual|pago mensual|mensualidad/i.test(lower) || lastAskedField === 'recurringPayments') && extractNumber(text))) {
    next.recurringPayments = extractNumber(text);
  }

  if ((/vale cada|valor de cada|cada recurrencia|cada recompra/i.test(lower) || lastAskedField === 'recurringValue') && extractNumber(text)) {
    next.recurringValue = extractNumber(text);
  }

  if ((/clientes nuevos|clientes al mes|cierres al mes|prospectos al mes|meta/i.test(lower) || lastAskedField === 'newClients') && extractNumber(text)) {
    next.newClients = extractNumber(text);
  }

  if ((/cierre|cierro|convierto|conversion|por ciento|porcentaje/i.test(lower) || lastAskedField === 'closeRate') && extractNumber(text)) {
    next.closeRate = extractNumber(text);
  }

  // Regex específico para qualifiedCpl: "calificado", "filtrado", "cita", "reunion", "sql", "mql"
  const isAboutQualified = /calificad|cualificad|filtrad|cita|reunion|sql|mql/i.test(lower);
  
  if (!isAboutQualified && (/cpl|costo por lead|costo por prospecto|lead cuesta|prospecto cuesta/i.test(lower) || lastAskedField === 'marketCpl') && extractNumber(text)) {
    next.marketCpl = extractNumber(text);
  }

  if (isAboutQualified && (/cpl|costo|cuesta|lead|prospecto/i.test(lower) || lastAskedField === 'qualifiedCpl') && extractNumber(text)) {
    next.qualifiedCpl = extractNumber(text);
  }

  if ((/reinvert|porcentaje|marketing y ventas|publicidad/i.test(lower) || lastAskedField === 'marketingPercent') && extractNumber(text)) {
    next.marketingPercent = extractNumber(text);
  }

  if (
    /semana|semanas|dias|urgente|este ano|este mes|en 30|en 60|en dos semanas|en 2 semanas|salir al mercado/i.test(lower) &&
    !/presupuesto|dolar|dolares|ticket|cliente|lead|cpl|mes de contrato|cada mes/i.test(lower)
  ) {
    next.desiredTimeline = text;
  } else if (lastAskedField === 'desiredTimeline' && isMeaningfulFreeText(text)) {
    next.desiredTimeline = text;
  }

  if (lastAskedField && !next[lastAskedField] && canAssignBareNumberToField(lastAskedField, text)) {
    next[lastAskedField] = extractNumber(text);
  }

  // B2: Detección de campos que ningún extractor cubría antes
  // hasGoogleMyBusiness — detectar Si/No en respuesta contextual
  if (lastAskedField === 'hasGoogleMyBusiness' || /google my business|ficha de google|mapa de google/i.test(lower)) {
    if (/\bsi\b|ya la tengo|ya tengo|ya esta|ya cuento|ya activa|claro que si/i.test(lower)) next.hasGoogleMyBusiness = 'Si';
    else if (/\bno\b|no la tengo|no tengo|aun no|todavia no|no cuento|no esta/i.test(lower)) next.hasGoogleMyBusiness = 'No';
  }

  // missingAssets — detectar qué activos le faltan crear
  if ((lastAskedField === 'missingAssets' || /me falta|no tengo|falta crear|necesito crear|no cuento con/i.test(lower)) && isMeaningfulFreeText(text) && !detectQuestionIntent(text)) {
    next.missingAssets = text;
  }

  // priorityOffering — detectar el producto/servicio estrella
  if ((lastAskedField === 'priorityOffering' || /mas quiero impulsar|producto estrella|servicio estrella|mas rentable|prioridad ahora|quiero destacar/i.test(lower)) && isMeaningfulFreeText(text) && !detectQuestionIntent(text)) {
    next.priorityOffering = text;
  }

  // B6: Extracción local de email y teléfono como red de seguridad si la IA falla
  if (!answers.userEmail) {
    const emailMatch = text.match(/[\w.+\-]+@[\w.\-]+\.[a-zA-Z]{2,}/);
    if (emailMatch) next.userEmail = emailMatch[0];
  }
  if (!answers.userPhone) {
    const phoneMatch = text.match(/\+?[\d][\d\s\-().]{6,}/);
    if (phoneMatch) next.userPhone = phoneMatch[0].trim();
  }

  return Object.fromEntries(
    Object.entries(next).filter(([, value]) => String(value ?? '').trim() !== '')
  );
};

export function sanitizeAnswers(answers = {}) {
  const next = { ...answers };

  // Valores placeholder que la IA pone cuando no tiene el dato— deben tratarse como vacíog
  const PLACEHOLDER_PATTERN = /^(none|n\/a|no aplica|no disponible|no sabe|desconocido|sin dato|nd|-)$/i;

  // Barrer todos los campos text-libre y limpiar placeholders
  Object.keys(next).forEach(key => {
    const val = cleanText(next[key]);
    if (PLACEHOLDER_PATTERN.test(val)) {
      next[key] = '';
    }
  });

  if (/^\d+$/.test(cleanText(next.objetivoPrincipal))) {
    next.objetivoPrincipal = '';
  }

  if (/presupuesto|dolar|dolares|cada mes/i.test(cleanText(next.desiredTimeline))) {
    next.desiredTimeline = '';
  }

  if (GREETING_PATTERN.test(cleanText(next.negocio))) {
    next.negocio = '';
  }

  if (/^\d+$/.test(cleanText(next.requestedService))) {
    next.requestedService = '';
  }

  if (/^\d+$/.test(cleanText(next.businessModelState))) {
    next.businessModelState = '';
  }

  if (/^\d+$/.test(cleanText(next.brandStatus))) {
    next.brandStatus = '';
  }

  return next;
}

export const mergeAnswers = (answers, extracted = {}, context = {}) => {
  const nextAnswers = { ...answers };
  const lastAskedField = context.lastAskedField || '';

  Object.entries(extracted).forEach(([key, value]) => {
    // Solo procesamos campos que existen en nuestro modelo oficial
    if (!FIELD_ORDER.includes(key)) return;

    const normalizedValue = normalizeFieldValue(key, value);
    if (cleanText(normalizedValue) === '') return; // ignorar valores vacíos

    const existingValue = cleanText(nextAnswers[key]);

    // A3: Protección de magnitud para campos numéricos críticos
    // Si ya hay un valor mayor guardado, solo lo reemplazamos si el campo era el que se preguntaba activamente
    // O si el usuario está explícitamente corrigiendo un dato
    const isCorrecting = context.userMessage && (NEGATION_PATTERN.test(cleanText(context.userMessage).toLowerCase()) || /cambia|corrige|no es|diferente|mentira|error/i.test(cleanText(context.userMessage).toLowerCase()));
    
    if (NUMERIC_FIELDS.has(key) && existingValue && !isCorrecting) {
      const existingNum = Number(existingValue.replace('%', ''));
      const newNum = Number(String(normalizedValue).replace('%', ''));
      if (existingNum > 0 && newNum > 0 && newNum < existingNum * 0.5 && key !== lastAskedField) {
        // Nuevo valor es menos de la mitad del existente y no era la pregunta activa → no sobrescribir
        return;
      }
    }

    nextAnswers[key] = normalizedValue;
  });

  return sanitizeAnswers(nextAnswers);
};

export const buildAssistantMetrics = (answers) =>
  calculateBusinessMetrics({
    ticket: answers.ticket,
    recurringPayments: answers.recurringPayments,
    recurringValue: answers.recurringValue,
    productionCost: answers.productionCost,
    newClients: answers.newClients,
    marketingPercent: answers.marketingPercent || 20,
    closeRate: answers.closeRate,
    marketCpl: answers.marketCpl,
    qualifiedCpl: answers.qualifiedCpl,
  });

export const hasEnoughForDiagnosis = (answers, messagesCount = 0) => {
  // 0. Si esta solo investigando, nunca hay diagnostico
  if (answers.isInvestigating === 'true') return false;

  // 1. Verificacion obligatoria de confirmacion final por el usuario
  if (answers.isConfirmed === 'true') return true;

  // Ya no retornamos true basados en conteos y metricas. Obligamos al LLM a pedir confirmación primero.
  return false;
};

export const buildAssistantSnapshot = (answers) => {
  const stageKey = determineJourneyStage(answers);
  const config = STAGE_CONFIG[stageKey];
  const completed = FIELD_ORDER.filter((field) => cleanText(answers[field]));

  return {
    stageKey,
    stageLabel: config.label,
    stageId: config.id,
    completedFields: completed,
    missingFields: getMissingFields(answers),
    missingCoreFields: getMissingCoreFields(answers),
    readyForDiagnosis: hasEnoughForDiagnosis(answers),
  };
};

const buildFieldCaptureSummary = (capturedKeys = []) => {
  if (!capturedKeys.length) return '';
  if (capturedKeys.length === 1) return `Ya tome ${FIELD_LABELS[capturedKeys[0]] || capturedKeys[0]}.`;
  return `Ya tome ${capturedKeys.map((key) => FIELD_LABELS[key] || key).join(', ')}.`;
};

const explainWhyWeAsk = (field, answers) => {
  const stage = determineJourneyStage(answers);
  const explanations = {
    problemaPrincipal: stage === STAGE_KEYS.BRAND
      ? 'Le pregunto esto para saber qué parte de su marca está incompleta: posicionamiento, identidad, web, o materiales.'
      : 'Se lo pregunto porque necesito detectar dónde se está perdiendo dinero: tráfico, seguimiento, cierre o estructura.',
    requestedService: 'Le formulo esta pregunta para ubicarle rápido en la etapa correcta y no recomendarle algo que todavía no le toca.',
    businessModelState: 'Necesito saber si ya existe base real o si todavía estamos montando el negocio, porque eso cambia toda la recomendación.',
    brandStatus: 'Con esto dimensiono si primero nos toca construir activos de marca o si ya podemos pasar directo a atracción y crecimiento.',
    ticket: 'El ticket promedio me dice cuánto deja un cliente cuando entra. Sin ese dato no le puedo calcular si invertir en captación le deja un margen sano.',
    marketCpl: 'El CPL o costo por lead es lo que le suele costar cada contacto. Si no conocemos su capacidad real, sería táctico, no estratégico.',
    closeRate: 'La tasa de cierre me permite pasar de leads a facturación. Sin ese porcentaje no le puedo calcular cuántos contactos necesita para su meta.',
    recurringPayments: 'La recurrencia de compra define el valor real de su cliente en el tiempo. Eso cambia por completo la estrategia de adquisición.',
    explain: 'Voy agrupando cada detalle que me da para decirle con claridad si la estrategia es viable o si hay que ordenar la base.',
  };
  return explanations[field] || explanations.explain;
};

export const buildNextQuestion = (answers) => {
  const config = getStageConfig(answers);
  const missing = getMissingFields(answers);
  if (!missing.length) return '';
  const question = config.questions[missing[0]] || '';
  const userName = cleanText(answers.userName) || 'estimado/a';
  return question.replace(/\{userName\}/g, userName);
};

export const buildDeterministicReply = (answers, extracted = {}, context = {}) => {
  const nextQuestion = buildNextQuestion(answers);
  
  if (GREETING_PATTERN.test(cleanText(context.userMessage))) {
    return ['¡Hola! Soy su asesor estrategico de Hitpoly.', nextQuestion || '¿En qué puedo ayudarle hoy?'].filter(Boolean);
  }

  // Fallback simple por si la IA falla
  return [
    Object.keys(extracted).length > 0 ? 'Entendido, he tomado nota.' : 'Le escucho con atencion.',
    nextQuestion || '¿Hay algo mas que deba saber de su negocio?'
  ].filter(Boolean);
};

const getServicesForStage = (stageKey) => {
  const config = STAGE_CONFIG[stageKey];
  const byId = new Map(
    stageCatalog.flatMap((stage) => stage.services.map((service) => [service.id, { ...service, phaseTitle: stage.title, phaseSummary: stage.summary }]))
  );
  return config.serviceIds.map((id) => byId.get(id)).filter(Boolean);
};

export const rankRecommendedServices = (answers, metrics) => {
  const stageKey = determineJourneyStage(answers);
  const stageServices = getServicesForStage(stageKey);
  const problem = cleanText(answers.problemaPrincipal).toLowerCase();
  const objective = cleanText(answers.objetivoPrincipal).toLowerCase();
  const assets = cleanText(answers.currentAssets).toLowerCase();

  return stageServices
    .map((service) => {
      const haystack = `${service.name} ${service.description} ${service.details}`.toLowerCase();
      let score = 10;
      if (problem && haystack.includes(problem.split(' ')[0])) score += 3;
      if (objective && haystack.includes(objective.split(' ')[0])) score += 2;
      if (assets && haystack.includes('web') && !assets.includes('web')) score += 2;
      if (metrics.marketingServicesBudget > 0 && service.price <= metrics.marketingServicesBudget) score += 2;
      return { ...service, score };
    })
    .sort((a, b) => b.score - a.score);
};

const buildFinancialSummaryLines = (metrics) => [
  `Pago inicial (ticket): ${formatCurrency(metrics.ticket)}`,
  `Pagos recurrentes en un ano: ${metrics.recurringPayments || 0}`,
  `Valor estimado de cada recurrencia: ${formatCurrency(metrics.recurringValue)}`,
  `Ingreso total por cliente (LTV): ${formatCurrency(metrics.ltv)}`,
  `Costo de produccion por cliente: ${formatCurrency(metrics.productionCost)}`,
  `Utilidad bruta por cliente: ${formatCurrency(metrics.grossProfitPerClient)}`,
  `Meta de clientes nuevos: ${metrics.newClients || 0}`,
  `Ganancia real proyectada: ${formatCurrency(metrics.projectedRealProfit)}`,
  `Porcentaje de inversion en marketing y ventas: ${metrics.marketingPercent || 0}%`,
  `Monto de inversion por cliente: ${formatCurrency(metrics.investmentPerClient)}`,
  `Presupuesto total por objetivo de ventas: ${formatCurrency(metrics.totalInvestmentBudget)}`,
  `Tasa de cierre comercial: ${metrics.closeRate || 0}%`,
  `CPA maximo: ${formatCurrency(metrics.cpaMax)}`,
  `Leads necesarios: ${metrics.leadsNeeded || 0}`,
  `CPL maximo: ${formatCurrency(metrics.cplMax)}`,
  `CPL de mercado estimado: ${formatCurrency(metrics.marketCpl)}`,
  `Ganancia bruta total: ${formatCurrency(metrics.grossRevenueTotal)}`,
  `Gasto de produccion total: ${formatCurrency(metrics.totalProductionCost)}`,
  `Servicios de marketing/agencia sugeridos: ${formatCurrency(metrics.marketingServicesBudget)}`,
  `Utilidad neta final: ${formatCurrency(metrics.finalNetProfit)}`,
  `Total fuera de todo gasto: ${formatCurrency(metrics.profitAfterAllCosts)}`,
  `ROI: ${metrics.roi.toFixed(2)}x`,
  `Estado del negocio: ${metrics.businessState}`,
];

export const buildFallbackDiagnosis = (answers) => {
  const stageKey = determineJourneyStage(answers);
  const metrics = buildAssistantMetrics(answers);
  const services = rankRecommendedServices(answers, metrics).slice(0, 4);
  const stageConfig = STAGE_CONFIG[stageKey];
  const serviceBudget = services.reduce((sum, service) => sum + Number(service.price || 0), 0);
  const threeMonthAgencyAndAds = (metrics.totalInvestmentBudget + metrics.marketingServicesBudget) * 3;
  const budget = Number(answers.presupuestoMesa || 0);
  const coversBrandBuild = budget >= serviceBudget;
  const coversThreeMonths = budget >= threeMonthAgencyAndAds;
  const viability = stageKey === STAGE_KEYS.BRAND ? coversBrandBuild : coversThreeMonths && metrics.viable;
  const missingCore = getMissingCoreFields(answers);

  const intro = viability
    ? `Viendo tu caso, si hay base para avanzar por ${stageConfig.label} con criterio.`
    : `Viendo tu caso, yo no empujaria esta etapa a ciegas porque hoy la caja o los numeros aun no la sostienen con comodidad.`;

  const analysis = stageKey === STAGE_KEYS.BRAND
    ? `En marca nos enfocamos en construir los activos que te faltan para tener una presencia sólida. Con un paquete sugerido de ${formatCurrency(serviceBudget)}, podemos dejar tu identidad lista para el mercado.`
    : `Basado en tu LTV de ${formatCurrency(metrics.ltv)} y una utilidad bruta por cliente de ${formatCurrency(metrics.grossProfitPerClient)}, tu modelo es ${metrics.viable ? 'viable' : 'ajustado'}. El CPL máximo permitido para mantener rentabilidad es de ${formatCurrency(metrics.cplMax)}.`;

  const moneyRead = stageKey === STAGE_KEYS.BRAND
    ? `Para dejar tu marca lista, el Alcance Estimado de Implementación de los activos faltantes es de ${formatCurrency(serviceBudget)}.`
    : `Hemos calculado un CPL estimado de ${formatCurrency(metrics.marketCpl)}, un Costo por Cliente (CAC) de ${formatCurrency(metrics.investmentPerClient)} y nuestros servicios mensuales de ${formatCurrency(metrics.marketingServicesBudget)}. Esto nos da un ALCANCE ESTIMADO de ${formatCurrency(metrics.marketCpl + metrics.marketingServicesBudget)} por mes para iniciar.`;

  const recommendations = [
    ...services.map(
      (service) => `${service.phaseTitle}: ${service.name}. Tiempo: ${service.time}. Inversión: ${formatCurrency(service.price)}.`
    )
  ];

  const closing = missingCore.length
    ? `Todavía me faltan algunos datos para darte la cotización formal, pero ya podemos ver el rumbo. ¿Te parece bien este alcance estimado para proceder?`
    : `¿Te parece bien este alcance estimado para proceder con la generación de tu cotización formal en PDF?`;

  return {
    intro,
    analysis,
    moneyRead,
    recommendations,
    closing,
    metrics,
    stageKey,
    stageLabel: stageConfig.label,
    coversBrandBuild,
    coversThreeMonths,
    serviceBudget,
    threeMonthAgencyAndAds,
    viable: viability,
  };
};

const assistantBrain = {
  STAGE_KEYS,
  FIELD_ORDER,
  FIELD_LABELS,
  createInitialAnswers,
  extractNumber,
  normalizeFieldValue,
  detectObjective,
  determineJourneyStage,
  getMissingCoreFields,
  getConversationChecklist,
  inferFieldsFromMessage,
  mergeAnswers,
  buildAssistantMetrics,
  hasEnoughForDiagnosis,
  buildAssistantSnapshot,
  rankRecommendedServices,
  buildNextQuestion,
  buildDeterministicReply,
  buildFallbackDiagnosis,
};

export default assistantBrain;
